import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { PageHeader, Card, Pill } from "@/dashboard/ui";
import { Users, UserPlus, CalendarClock, BadgeCheck, PhoneCall, CalendarCheck, Megaphone, ArrowRight } from "lucide-react";

export default function Overview() {
  const [m, setM] = useState(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => setM({}));
  }, []);

  const cards = [
    { label: "Total Leads", value: m?.total_leads, icon: Users, testid: "metric-total-leads" },
    { label: "New Leads", value: m?.new_leads, icon: UserPlus, testid: "metric-new-leads" },
    { label: "Follow-ups Due", value: m?.followups_due, icon: CalendarClock, testid: "metric-followups-due" },
    { label: "Converted Leads", value: m?.converted_leads, icon: BadgeCheck, testid: "metric-converted-leads" },
    { label: "Total Calls", value: m?.total_calls, icon: PhoneCall, testid: "metric-total-calls", offline: m && !m.calls_connected },
    { label: "Upcoming Appointments", value: m?.appointments_booked, icon: CalendarCheck, testid: "metric-appointments" },
    { label: "Marketing Campaigns", value: m?.marketing_campaigns, icon: Megaphone, testid: "metric-campaigns" },
  ];

  return (
    <div data-testid="overview-page">
      <PageHeader title="Overview" sub="Everything below is counted from your real saved records — nothing is simulated." />

      {!m ? (
        <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
      ) : (
        <div className="grid grid-cols-2 gap-3.5 sm:gap-4 lg:grid-cols-4">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.label}
                initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: reduce ? 0 : i * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card className="h-full p-5 transition-shadow hover:shadow-[0_16px_36px_-20px_rgba(0,0,0,0.2)]" data-testid={c.testid}>
                  <div className="flex items-center justify-between">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-100 text-[#111111]">
                      <Icon size={16} />
                    </span>
                    {c.offline && <Pill tone="outline">Not connected</Pill>}
                  </div>
                  <p className="mt-4 text-3xl font-extrabold tracking-tight text-[#111111]">{c.value ?? 0}</p>
                  <p className="mt-1 text-xs font-medium text-neutral-500">{c.label}</p>
                </Card>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduce ? 0 : cards.length * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/dashboard/leads"
              data-testid="overview-add-lead-link"
              className="flex h-full flex-col justify-between rounded-2xl bg-[#111111] p-5 text-white transition-colors hover:bg-neutral-800"
            >
              <ArrowRight size={18} />
              <div>
                <p className="mt-4 text-sm font-bold">Manage your leads</p>
                <p className="mt-1 text-xs text-neutral-400">Add, edit and follow up</p>
              </div>
            </Link>
          </motion.div>
        </div>
      )}
    </div>
  );
}
